import { startOfDay, subDays } from "date-fns";
import { LineChart as LineChartIcon, ListFilter } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  SEGMENT_CHART_PALETTE,
  SegmentCountChartBody,
} from "./SegmentCountChartBody";
import { SegmentCountFilterMenu } from "./SegmentCountFilterMenu";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/v2/card";
import type { ChartConfig } from "@/components/ui/v2/chart";
import {
  DateRangePicker,
  DateRangeQuickFilters,
} from "@/components/ui/v2/date-range-picker";
import EmptyState from "@/components/ui/v2/empty-state";
import { Skeleton } from "@/components/ui/v2/skeleton";
import { useSegmentMembershipHistory } from "@/features/home/api/useSegmentMembershipHistory";
import { useDateRangeZoom } from "@/features/home/hooks/useDateRangeZoom";
import { useSegmentsInfinite } from "@/features/segments/api/useSegmentsInfinite";
import { useInfiniteScroll } from "@/lib/hooks/useInfiniteScroll";
import { useVisibleEntities } from "@/lib/hooks/useVisibleEntities";

const DEFAULT_TOP_N = 4;
const DEFAULT_DAYS_BACK = 7;
const DATE_PRESETS = DateRangeQuickFilters.all;

interface SegmentItem {
  id: string;
  name: string;
  count: number;
}

interface ChartRowDatum {
  day: string;
  [segmentId: string]: number | string | null;
}

function buildDefaultRange(): { from: Date; to: Date } {
  const to = startOfDay(new Date());
  const from = subDays(to, DEFAULT_DAYS_BACK - 1);
  return { from, to };
}

export function SegmentCountChart({ clientId }: { clientId?: string }) {
  const { t, i18n } = useTranslation();
  const segmentsQuery = useSegmentsInfinite({
    clientId,
    pageSize: 20,
  });

  const { handleScroll } = useInfiniteScroll({
    onFetchMore: () => segmentsQuery.fetchNextPage(),
    hasMore: segmentsQuery.hasNextPage,
    isLoading: segmentsQuery.isLoading,
    isFetching: segmentsQuery.isFetchingNextPage,
  });

  const [range, setRange] = useState<{ from: Date; to: Date }>(() =>
    buildDefaultRange(),
  );

  const dayFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        day: "numeric",
        month: "short",
      }),
    [i18n.language],
  );

  const formatDay = (day: string) => {
    const [y, m, d] = day.split("-").map(Number);
    return dayFormatter.format(new Date(y, m - 1, d));
  };

  const { drag, onMouseDown, onMouseMove, onMouseUp } = useDateRangeZoom({
    onZoom: setRange,
  });

  const allSegments = useMemo<SegmentItem[]>(() => {
    const list = segmentsQuery.data?.pages.flatMap((p) => p.data) ?? [];
    return [...list]
      .map((s) => ({ id: s.id, name: s.name, count: s.count ?? 0 }))
      .sort((a, b) => b.count - a.count);
  }, [segmentsQuery.data]);

  const {
    filterActive,
    isSelected,
    toggle,
    reset,
    visibleItems: filteredSegments,
  } = useVisibleEntities<SegmentItem>({
    items: allSegments,
    storageKey: "home.segmentCount.visible",
    getKey: (s) => s.id,
    defaultSelection: (items) =>
      items.slice(0, DEFAULT_TOP_N).map((s) => s.id),
  });

  const visibleSegments = useMemo(
    () =>
      filteredSegments.map((s) => ({
        id: s.id,
        name: s.name,
        baselineCount: s.count,
      })),
    [filteredSegments],
  );

  const {
    series,
    days,
    isLoading: historyLoading,
  } = useSegmentMembershipHistory({
    segments: visibleSegments,
    from: range.from,
    to: range.to,
    clientId,
  });

  const isLoading = segmentsQuery.isLoading || historyLoading;

  const { rows, chartConfig, hasData } = useMemo(() => {
    if (visibleSegments.length === 0)
      return {
        rows: [] as ChartRowDatum[],
        chartConfig: {} as ChartConfig,
        hasData: false,
      };

    const config: ChartConfig = {};
    visibleSegments.forEach((s, i) => {
      config[s.id] = {
        label: s.name,
        color: SEGMENT_CHART_PALETTE[i % SEGMENT_CHART_PALETTE.length],
      };
    });

    const datapoints: ChartRowDatum[] = days.map((day) => {
      const row: ChartRowDatum = { day };
      for (const { segment, points } of series) {
        const point = points.find((p) => p.day === day);
        row[segment.id] = point?.count ?? null;
      }
      return row;
    });

    const totals = datapoints.reduce((acc, row) => {
      let sum = 0;
      for (const { segment } of series) {
        sum += Number(row[segment.id] ?? 0);
      }
      return acc + sum;
    }, 0);

    return {
      rows: datapoints,
      chartConfig: config,
      hasData: totals > 0,
    };
  }, [series, days, visibleSegments]);

  const filteredEverythingOut =
    !isLoading && allSegments.length > 0 && visibleSegments.length === 0;

  // Key forces the DateRangePicker to remount when range changes programmatically
  // (e.g. from zoom) so its internal state reflects the new initial values.
  const datePickerKey = `${range.from.getTime()}-${range.to.getTime()}`;

  return (
    <Card padding="md" className="py-5">
      <CardHeader className="flex flex-col items-start justify-between gap-8 px-5 space-y-0">
        <div className="flex flex-col gap-1">
          <CardTitle className="text-lg">
            {t("home.segmentCount.title")}
          </CardTitle>
          <CardDescription>{t("home.segmentCount.subtitle")}</CardDescription>
        </div>
        <div className="flex justify-between items-center gap-2 w-full">
          <DateRangePicker
            key={datePickerKey}
            initialDateFrom={range.from}
            initialDateTo={range.to}
            align="end"
            maxDate={startOfDay(new Date())}
            allowedPresetIds={DATE_PRESETS}
            onUpdate={({ range: r }) => {
              if (r.from && r.to) setRange({ from: r.from, to: r.to });
            }}
            className="h-8 px-2.5 text-xs"
          />
          {allSegments.length > 0 && (
            <SegmentCountFilterMenu
              segments={allSegments}
              filterActive={filterActive}
              defaultTopN={DEFAULT_TOP_N}
              isSelected={isSelected}
              toggle={toggle}
              reset={reset}
              onScroll={handleScroll}
              isFetchingNextPage={segmentsQuery.isFetchingNextPage}
              t={t}
            />
          )}
        </div>
      </CardHeader>
      <CardContent className="px-5">
        {isLoading ? (
          <Skeleton className="h-[280px] w-full" />
        ) : filteredEverythingOut ? (
          <EmptyState
            icon={ListFilter}
            title={t("home.segmentCount.filter.emptyTitle")}
            description={t("home.segmentCount.filter.emptyDescription")}
            action={{
              label: t("home.segmentCount.filter.reset", { max: DEFAULT_TOP_N }),
              onClick: reset,
            }}
          />
        ) : !hasData ? (
          <EmptyState
            icon={LineChartIcon}
            title={t("home.segmentCount.empty.title")}
            description={t("home.segmentCount.empty.description")}
          />
        ) : (
          <SegmentCountChartBody
            rows={rows}
            chartConfig={chartConfig}
            visibleSegments={visibleSegments}
            formatDay={formatDay}
            drag={drag}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
          />
        )}
      </CardContent>
    </Card>
  );
}
